/**
 * W-9 Form Models
 * Following the same clean architecture pattern as W8BEN and W8ECI models
 */

import { parseAddressComponents, isUSStateOrTerritory } from "../utils/formUtils";

// W9 Response Model - Fields that come from API endpoint
export class W9ResponseModel {
  // Basic fields typically from API
  "UniqueIdentifier": string = "";
  "entityUniqueIdentifier": string = "";
  "Name of organization": string = "";
  "First name and middle name": string = "";
  "Last name": string = "";
  "W9-Business_name": string = "";
  "Permanent residence address": string = "";
  "Permanent city address": string = "";
  "Permanent address country": string = "";
  "Social security number": string = "";
  "Employer identification number": string = "";
  "Form_number": string = "";
  "Form_revision_date": string = "";

  // W9 specific fields
  "W9-Tax_classification_individual_sole_proprietor": string = "";
  "W9-Tax_classification_c_corporation": string = "";
  "W9-Tax_classification_s_corporation": string = "";
  "W9-Tax_classification_partnership": string = "";
  "W9-Tax_classification_trust_estate": string = "";
  "W9-Tax_classification_llc": string = "";
  "W9_Tax_classification_llc_value": string = "";
  "W9-Tax_classification_other": string = "";
  "W9-Tax_classification_other_description": string = "";
  "W9-3B_foreign_partners": string = "";
  "W9-Exempt_payee_code": string = "";
  "W9-Fatca_exemption_code": string = "";
  "W9-Requester_name_address": string = "";
  "W9-Account_numbers": string = "";
  "Chapter4Status": string = "";

  // Signature fields
  "Print_name": string = "";
  "Signature": string = "";
  "Signature_date": string = "";
  "Signature_unreadable": boolean = false;
  "needToBeSigned": string = "yes";
  "isPartII2aCrossedOut": string = "no";
  "isPartII2bCrossedOut": string = "no";
  "isPartII2cCrossedOut": string = "no";
  "isSigned": string = "no";
  "isModified": string = "no";
  "FormName": string = "W-9";

  // Dynamic fields support
  [key: string]: any;

  constructor(data?: any) {
    if (data) {
      Object.assign(this, data);
    }
  }
}

// W9 Form Model - Complete form fields (more comprehensive)
export class W9FormModel {
  // Basic identification fields
  "UniqueIdentifier": string = "";
  "entityUniqueIdentifier": string = "";
  "Form_number": string = "";
  "Form_revision_date": string = "";

  // Part I: Name and Entity Type
  "Name of organization": string = "";
  "First name and middle name": string = "";
  "Last name": string = "";
  "W9-Business_name": string = "";

  // Tax Classification Entity Type (checkboxes)
  "W9-Tax_classification_individual_sole_proprietor": boolean = false;
  "W9-Tax_classification_c_corporation": boolean = false;
  "W9-Tax_classification_s_corporation": boolean = false;
  "W9-Tax_classification_partnership": boolean = false;
  "W9-Tax_classification_trust_estate": boolean = false;
  "W9-Tax_classification_llc": boolean = false;
  "W9_Tax_classification_llc_value": string = "";
  "W9-Tax_classification_other": boolean = false;
  "W9-Tax_classification_other_description": string = "";

  // US Federal Tax Classifications (separate from User Record types)
  "W9-Federal_s_corporation": boolean = false;
  "W9-Federal_c_corporation": boolean = false;
  "W9-Federal_partnership": boolean = false;

  // Foreign Partners - using string to match form UI ('yes'/'no')
  "W9-3B_foreign_partners": string = "";

  // Exemption Codes
  "W9-Exempt_payee_code": string = "";
  "Exempt payee multiple": boolean = false;
  "Exempt payee multiple text": string = "";
  "Exempt payee unknown": boolean = false;
  "W9-Fatca_exemption_code": string = "";
  "FATCA multiple": boolean = false;
  "FATCA multiple text": string = "";
  "FATCA unknown": boolean = false;

  // Address fields - comprehensive
  "Permanent residence address": string = "";
  "Permanent address line 2": string = "";
  "Permanent address line 3": string = "";
  "Permanent address city": string = "";
  "Permanent address state USCA": string = "";
  "Permanent address state NonUS": string = "";
  "Permanent address zip": string = "";
  "Permanent address country": string = "";
  "Permanent address other": string = "";
  "Permanent address is care of": string = "no";
  "Permanent address is po box": string = "no";
  "Permanent address is rural route": string = "no";

  // Requestor Information
  "W9-Requester_name_address": string = "";
  "W9-Account_numbers": string = "";

  // Chapter 4 Status
  "Chapter4Status": string = "";

  // Taxpayer Identification Number
  "Taxpayer email": string = "";
  "Taxpayer type": string = "";
  "Taxpayer applied for": string = "Unknown";
  "Social security number": string = "";
  "Employer identification number": string = "";

  // Signature and certification
  "Print_name": string = "";
  "Signature": string = "";
  "Signature_date": string = "";
  "needToBeSigned": string = "yes";
  "isPartII2aCrossedOut": string = "no";
  "isPartII2bCrossedOut": string = "no";
  "isPartII2cCrossedOut": string = "no";
  "isSigned": string = "no";
  "isModified": string = "no";
  "FormName": string = "W-9";

  // Mapping control field - prevents re-mapping when already done
  isMapDone: boolean = false;

  // Dynamic fields support
  [key: string]: any;

  constructor(data?: any) {
    if (data) {
      Object.assign(this, data);
    }
  }

  /**
   * Validate the form data
   */
  validate(): ValidationResult {
    const errors: string[] = [];
    const fieldErrors: Record<string, string> = {};

    // Required field validations - Updated per user requirements
    // Permanent address, Date of birth, and Country of citizenship removed as mandatory per user request
    const requiredFields = ["UniqueIdentifier", "Name of organization"];

    // Conditional required fields based on tax classification
    if (this["W9-Tax_classification_individual_sole_proprietor"]) {
      requiredFields.push("First name and middle name", "Last name");
      // Remove organization name requirement for individuals
      const orgIndex = requiredFields.indexOf("Name of organization");
      if (orgIndex > -1) requiredFields.splice(orgIndex, 1);
    }

    requiredFields.forEach((field) => {
      const value = (this as any)[field];
      if (!value || (typeof value === "string" && value.trim() === "")) {
        const fieldName = field.replace("W9-", "").replace(/_/g, " ");
        const error = `${fieldName} is required`;
        errors.push(error);
        fieldErrors[field] = error;
      }
    });

    // Tax classification validation - at least one must be selected
    const taxClassifications = [
      "W9-Tax_classification_individual_sole_proprietor",
      "W9-Tax_classification_c_corporation",
      "W9-Tax_classification_s_corporation",
      "W9-Tax_classification_partnership",
      "W9-Tax_classification_trust_estate",
      "W9-Tax_classification_llc",
      "W9-Tax_classification_other",
    ];

    const hasTaxClassification = taxClassifications.some((field) => this[field as keyof this]);
    if (!hasTaxClassification) {
      const error = "Tax classification is required";
      errors.push(error);
      taxClassifications.forEach((field) => {
        fieldErrors[field] = error;
      });
    }

    // Taxpayer ID validation - either SSN or EIN required
    const hasSSN =
      this["Social security number"] && this["Social security number"].toString().trim();
    const hasEIN =
      this["Employer identification number"] &&
      this["Employer identification number"].toString().trim();
    if (!hasSSN && !hasEIN) {
      const error = "Either Social Security Number or Employer Identification Number is required";
      errors.push(error);
      fieldErrors["Social security number"] = error;
      fieldErrors["Employer identification number"] = error;
    }

    // Certification validation removed per user request

    return {
      isValid: errors.length === 0,
      errors,
      fieldErrors,
    };
  }

  /**
   * Check if a date string is valid
   */
  private isValidDate(dateString: string): boolean {
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime());
  }
}

export class ValidationResult {
  isValid: boolean = false;
  errors: string[] = [];
  fieldErrors: Record<string, string> = {};
}

/**
 * W9 Field Mapper - Maps response model fields to form model fields
 */
export class W9FieldMapper {
  /**
   * Map response model to form model
   */
  static mapResponseToForm(responseModel: W9ResponseModel): W9FormModel {
    const formModel = new W9FormModel();

    // Check if mapping is already done to prevent re-mapping
    if (formModel.isMapDone) {
      console.log("W9 mapping already done, skipping...");
      return formModel;
    }

    // Direct field mappings (fields that exist in both models)
    const directMappings = [
      "UniqueIdentifier",
      "entityUniqueIdentifier",
      "Form_number",
      "Form_revision_date",
      "Name of organization",
      "First name and middle name",
      "Last name",
      "W9-Business_name",
      "Permanent residence address",
      "Permanent address country",
      "Social security number",
      "Employer identification number",
      "W9-Tax_classification_individual_sole_proprietor",
      "W9-Tax_classification_c_corporation",
      "W9-Tax_classification_s_corporation",
      "W9-Tax_classification_partnership",
      "W9-Tax_classification_trust_estate",
      "W9-Tax_classification_llc",
      "W9_Tax_classification_llc_value",
      "W9-Tax_classification_other",
      "W9-Tax_classification_other_description",
      "W9-3B_foreign_partners",
      "W9-Exempt_payee_code",
      "W9-Fatca_exemption_code",
      "W9-Requester_name_address",
      "W9-Account_numbers",
      "Chapter4Status",
      "Print_name",
      "Signature",
      "Signature_date",
      "needToBeSigned",
      "isPartII2aCrossedOut",
      "isPartII2bCrossedOut",
      "isPartII2cCrossedOut",
      "isSigned",
      "isModified",
      "FormName",
    ];

    directMappings.forEach((field) => {
      if (responseModel[field] !== undefined) {
        formModel[field] = responseModel[field];
      }
    });

    // Special mappings
    this.mapSpecialFields(responseModel, formModel);

    // Handle dynamic fields from response
    Object.keys(responseModel).forEach((key) => {
      if (!directMappings.includes(key) && responseModel[key] !== undefined) {
        formModel[key] = responseModel[key];
      }
    });

    // Auto-set isSigned and Signature_unreadable based on Signature field (only during initial mapping)
    if (formModel.Signature && formModel.Signature.trim() !== "") {
      formModel.isSigned = "yes";
      formModel.Signature_unreadable = true;
    }

    // Set mapping done flag to prevent re-mapping
    formModel.isMapDone = true;
    console.log("W9 field mapping completed");

    return formModel;
  }

  /**
   * Handle special field mappings and transformations
   */
  private static mapSpecialFields(responseModel: W9ResponseModel, formModel: W9FormModel): void {
    // Helper function to check if a value is truthy (handles both string and boolean)
    const isTruthyValue = (value: any): boolean => {
      return (
        value === "selected" || value === "true" || value === true || value === 1 || value === "yes"
      );
    };

    // Map name fields - Handle individual vs organization names
    if (responseModel["Name of organization"]) {
      const isIndividualClassification =
        responseModel["W9-Tax_classification_individual_sole_proprietor"];
      if (isTruthyValue(isIndividualClassification)) {
        // Split organization name into first/last for individuals
        const fullName = responseModel["Name of organization"];
        const nameParts = fullName.split(" ");
        if (nameParts.length >= 2) {
          formModel["First name and middle name"] = nameParts.slice(0, -1).join(" ");
          formModel["Last name"] = nameParts[nameParts.length - 1];
          formModel["Name of organization"] = "";
        } else {
          formModel["First name and middle name"] = fullName;
          formModel["Last name"] = "";
          formModel["Name of organization"] = "";
        }
      }
    }

    if (responseModel["Permanent city address"]) {
      const addressComponents = parseAddressComponents(responseModel["Permanent city address"]);
      formModel["Permanent address city"] = addressComponents.city;

      // Check if state is US state/territory and set appropriate field
      const isUSState = isUSStateOrTerritory(addressComponents.state);
      if (isUSState) {
        formModel["Permanent address state USCA"] = addressComponents.state;
        formModel["Permanent address state NonUS"] = "";
      } else {
        formModel["Permanent address state NonUS"] = addressComponents.state;
        formModel["Permanent address state USCA"] = "";
      }

      formModel["Permanent address zip"] = addressComponents.postalCode;
    }

    // Auto-detect taxpayer type
    if (
      responseModel["Employer identification number"] &&
      responseModel["Employer identification number"].toString().trim()
    ) {
      formModel["Taxpayer type"] = "EIN";
    } else if (
      responseModel["Social security number"] &&
      responseModel["Social security number"].toString().trim()
    ) {
      formModel["Taxpayer type"] = "SSN/ITIN";
    }

    const booleanFields = [
      "W9-Tax_classification_individual_sole_proprietor",
      "W9-Tax_classification_c_corporation",
      "W9-Tax_classification_s_corporation",
      "W9-Tax_classification_partnership",
      "W9-Tax_classification_trust_estate",
      "W9-Tax_classification_llc",
      "W9-Tax_classification_other",
    ];

    booleanFields.forEach((field) => {
      const value = responseModel[field];
      formModel[field] = isTruthyValue(value);
    });

    // Handle foreign partners field separately - keep as string for UI compatibility
    if (responseModel["W9-3B_foreign_partners"] !== undefined) {
      const value = responseModel["W9-3B_foreign_partners"];
      // Convert various input formats to yes/no strings
      if (isTruthyValue(value)) {
        formModel["W9-3B_foreign_partners"] = "yes";
      } else if (value === "no" || value === "" || value === null) {
        formModel["W9-3B_foreign_partners"] = "no";
      } else {
        // Pass through the original value if it's already in the expected format
        formModel["W9-3B_foreign_partners"] = value.toString();
      }
    }

    // Reset the three federal checkboxes first
    formModel["W9-Federal_c_corporation"] = false;
    formModel["W9-Federal_s_corporation"] = false;
    formModel["W9-Federal_partnership"] = false;

    // Check for direct tax classification selections and auto-select corresponding federal classifications
    if (isTruthyValue(responseModel["W9-Tax_classification_c_corporation"])) {
      formModel["W9-Federal_c_corporation"] = true;
    }

    if (isTruthyValue(responseModel["W9-Tax_classification_s_corporation"])) {
      formModel["W9-Federal_s_corporation"] = true;
    }

    if (isTruthyValue(responseModel["W9-Tax_classification_trust_estate"])) {
      formModel["W9-Federal_c_corporation"] = false;
      formModel["W9-Federal_s_corporation"] = false;
      formModel["W9-Federal_partnership"] = false;
    }
    if (isTruthyValue(responseModel["W9-Tax_classification_individual_sole_proprietor"])) {
      formModel["W9-Federal_c_corporation"] = false;
      formModel["W9-Federal_s_corporation"] = false;
      formModel["W9-Federal_partnership"] = false;
    }

    // Process LLC value to auto-select appropriate federal tax classification checkbox (if LLC is selected)
    const llcValueField =
      responseModel["W9_Tax_classification_llc_value"] ||
      responseModel["W9-Tax_classification_llc_value"];

    if (llcValueField && isTruthyValue(responseModel["W9-Tax_classification_llc"])) {
      const llcValue = llcValueField.toString().toLowerCase();

      // Override federal selections based on LLC value
      formModel["W9-Federal_c_corporation"] = false;
      formModel["W9-Federal_s_corporation"] = false;
      formModel["W9-Federal_partnership"] = false;

      // Select appropriate checkbox based on LLC value
      if (llcValue.includes("p")) {
        formModel["W9-Federal_partnership"] = true;
      } else if (llcValue.includes("c")) {
        formModel["W9-Federal_c_corporation"] = true;
      } else if (llcValue.includes("s")) {
        formModel["W9-Federal_s_corporation"] = true;
      }
    }

    // Map additional response fields to form fields
    Object.keys(responseModel).forEach((responseField) => {
      const value = responseModel[responseField];
      if (value !== undefined && value !== null && value !== "") {
        if ((formModel as any)[responseField] !== undefined && responseField !== "isSigned") {
          (formModel as any)[responseField] = value;
        }

        // Handle API fields with underscores/dashes - map to form fields with spaces
        const formFieldName = responseField.replace(/_/g, " ").replace(/-/g, " ");
        if (formFieldName !== responseField && (formModel as any)[formFieldName] !== undefined) {
          (formModel as any)[formFieldName] = value;
        }
      }
    });

    // Auto-set isSigned based on Signature field (AFTER field mapping to avoid override)
    if (responseModel["Signature"] && responseModel["Signature"].toString().trim()) {
      formModel["isSigned"] = "yes";
    } else {
      formModel["isSigned"] = "no";
    }
  }
}
